// src/components/admin/ManageUsers.js
import React, { useState, useEffect } from "react";
import {
	Box,
	Paper,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	MenuItem,
	IconButton,
	Button,
	Alert,
	FormControl,
	InputLabel,
	Select,
	Chip,
	Autocomplete,
	TablePagination,
	Stack,
	Tooltip,
} from "@mui/material";
import {
	Edit as EditIcon,
	Check as CheckIcon,
	X as XIcon,
	Search as SearchIcon,
	Trash as TrashIcon,
	Download as DownloadIcon,
	Key as KeyIcon,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { ROLES as APP_ROLES } from "../../config/supabase";
import { fetchActiveSchoolsForExpert } from "../../utils/employeeSchools";
import { getNetlifyFunctionUrl } from "../../utils/netlifyFunctions";

const ROLES = {
	ROLE_SCHOOL_TEACHER: { label: "Teacher", color: "#10B981" },
	ROLE_FUNMATH_ADMIN: { label: "Admin", color: "#6366F1" },
	ROLE_FUNMIYO_EMPLOYEE: { label: "Expert", color: "#F59E0B" },
	ROLE_SCHOOL_ADMIN: { label: "School Admin", color: "#EF4444" },
};

const STATUS_COLORS = {
	Active: "#10B981",
	"Pending Approval": "#F59E0B",
	Inactive: "#EF4444",
};

const ManageUsers = () => {
	const { user, supabase } = useAuth();
	const [users, setUsers] = useState([]);
	const [filteredUsers, setFilteredUsers] = useState([]);
	const [selectedUser, setSelectedUser] = useState(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");
	const [schools, setSchools] = useState([]);
	const [grades, setGrades] = useState([]);

	// Pagination states
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const [filters, setFilters] = useState({
		role: "",
		status: "",
		search: "",
	});

	const [editForm, setEditForm] = useState({
		full_name: "",
		email: "",
		phone_number: "",
		role: "",
		school_id: "",
		grade_ids: [],
	});

	const [loggedInUserProfile, setLoggedInUserProfile] = useState(null);
	const [assignedSchoolIds, setAssignedSchoolIds] = useState([]);

	const [deleteDialog, setDeleteDialog] = useState(false);
	const [userToDelete, setUserToDelete] = useState(null);
	const [deleting, setDeleting] = useState(false);

	const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
	const [userToResetPassword, setUserToResetPassword] = useState(null);
	const [resettingPassword, setResettingPassword] = useState(false);

	const isFunmiyoAdmin = loggedInUserProfile?.role === APP_ROLES.FUNMIYO_ADMIN;
	const isFunmiyoEmployee =
		loggedInUserProfile?.role === APP_ROLES.FUNMIYO_EMPLOYEE;

	const employeeCanManageProfile = (profile) => {
		if (!isFunmiyoEmployee) return true;
		if (assignedSchoolIds.length === 0) return false;
		return (
			profile?.role === APP_ROLES.SCHOOL_TEACHER &&
			assignedSchoolIds.includes(profile?.school_id)
		);
	};

	useEffect(() => {
		if (!user?.id) return;

		const load = async () => {
			try {
				const { data: me, error: meErr } = await supabase
					.from("profiles")
					.select("id, role, full_name, email, school_id")
					.eq("id", user.id)
					.single();

				if (meErr) throw meErr;
				setLoggedInUserProfile(me);

				let employeeSchoolIds = [];
				if (me?.role === APP_ROLES.FUNMIYO_EMPLOYEE) {
					const activeSchools = await fetchActiveSchoolsForExpert(
						supabase,
						user.id
					);
					employeeSchoolIds = activeSchools.map((school) => school.id);
					setAssignedSchoolIds(employeeSchoolIds);
					setSchools(
						activeSchools.map((school) => ({
							id: school.id,
							name: school.name,
						}))
					);
				}

				await Promise.all([
					fetchUsers(me, employeeSchoolIds),
					me?.role === APP_ROLES.FUNMIYO_EMPLOYEE
						? Promise.resolve()
						: fetchSchools(me),
					fetchGrades(),
				]);
			} catch (e) {
				console.error("ManageUsers: init", e);
			}
		};

		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.id]);

	// Add this function after handleStatusChange
	const handleDeleteUser = async () => {
		if (!userToDelete || !isFunmiyoAdmin) return;

		setDeleting(true);
		try {
			// Delete related records first
			await supabase
				.from("teacher_grades")
				.delete()
				.eq("profile_id", userToDelete.id);

			// Delete the user profile
			const { error } = await supabase
				.from("profiles")
				.delete()
				.eq("id", userToDelete.id);

			if (error) throw error;

			setSuccessMessage("User deleted successfully");
			await fetchUsers(loggedInUserProfile);
			setDeleteDialog(false);
			setUserToDelete(null);

			setTimeout(() => setSuccessMessage(""), 3000);
		} catch (err) {
			console.error("Error deleting user:", err);
		} finally {
			setDeleting(false);
		}
	};

	const handleResetPassword = async () => {
		if (!userToResetPassword) return;

		if (!employeeCanManageProfile(userToResetPassword)) {
			setSuccessMessage("You are not allowed to reset this user's password.");
			setTimeout(() => setSuccessMessage(""), 4000);
			return;
		}

		setResettingPassword(true);
		try {
			const defaultPassword = "123456";
			const {
				data: { session },
			} = await supabase.auth.getSession();

			const headers = {
				"Content-Type": "application/json",
			};
			if (session?.access_token) {
				headers.Authorization = `Bearer ${session.access_token}`;
			}

			const response = await fetch(
				getNetlifyFunctionUrl("reset-user-password"),
				{
					method: "POST",
					headers,
					body: JSON.stringify({
						userId: userToResetPassword.id,
						email: userToResetPassword.email,
						newPassword: defaultPassword,
					}),
				}
			);

			const responseText = await response.text();
			let data = null;
			try {
				data = responseText ? JSON.parse(responseText) : null;
			} catch {
				throw new Error(
					responseText?.slice(0, 200) ||
						"Password reset service unavailable. Restart the dev server after pulling latest changes."
				);
			}

			if (!response.ok) {
				throw new Error(
					data?.message || data?.error || "Failed to reset password"
				);
			}

			setSuccessMessage(`Password reset successfully for ${userToResetPassword.email}. New password: ${defaultPassword}`);
			setResetPasswordDialog(false);
			setUserToResetPassword(null);
			setTimeout(() => setSuccessMessage(""), 5000);
		} catch (err) {
			console.error("Error resetting password:", err);
			setSuccessMessage(`Error: ${err.message || 'Failed to reset password. Please check Netlify function configuration.'}`);
			setTimeout(() => setSuccessMessage(""), 8000);
		} finally {
			setResettingPassword(false);
		}
	};

	useEffect(() => {
		applyFilters();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters, users]);

	const fetchGrades = async () => {
		try {
			const { data, error } = await supabase.from("grades").select("id, name");
			if (error) throw error;
			setGrades(data || []);
		} catch (err) {
			console.error("Error fetching grades:", err);
		}
	};

	const fetchUsers = async (me, employeeSchoolIds = assignedSchoolIds) => {
		try {
			let query = supabase
				.from("profiles")
				.select(
					`
					*,
					schools!profiles_school_id_fkey (id, name),
					teacher_grades (grades (id, name))
					`
				)
				.order("created_at", { ascending: false });

			if (me?.role === APP_ROLES.FUNMIYO_EMPLOYEE) {
				if (employeeSchoolIds.length === 0) {
					setUsers([]);
					setFilteredUsers([]);
					return;
				}
				query = query
					.eq("role", APP_ROLES.SCHOOL_TEACHER)
					.in("school_id", employeeSchoolIds);
			}

			const { data, error } = await query;

			if (error) throw error;
			setUsers(data || []);
			setFilteredUsers(data || []);
		} catch (err) {
			console.error("Error fetching users:", err);
		}
	};

	const fetchSchools = async (me) => {
		try {
			if (me?.role === APP_ROLES.FUNMIYO_EMPLOYEE) {
				const activeSchools = await fetchActiveSchoolsForExpert(supabase, user.id);
				setSchools(
					activeSchools.map((school) => ({
						id: school.id,
						name: school.name,
					}))
				);
				return;
			}

			const { data, error } = await supabase
				.from("schools")
				.select("id, name")
				.eq("status", "Active")
				.order("name");
			if (error) throw error;
			setSchools(data || []);
		} catch (err) {
			console.error("Error fetching schools:", err);
		}
	};

	const applyFilters = () => {
		let filtered = [...users];

		if (filters.role) {
			filtered = filtered.filter((user) => user.role === filters.role);
		}

		if (filters.status) {
			filtered = filtered.filter((user) => user.status === filters.status);
		}

		if (filters.search) {
			const searchLower = filters.search.toLowerCase();
			filtered = filtered.filter(
				(user) =>
					user.email?.toLowerCase().includes(searchLower) ||
					user.full_name?.toLowerCase().includes(searchLower) ||
					user.phone_number?.toLowerCase().includes(searchLower) ||
					ROLES[user.role]?.label.toLowerCase().includes(searchLower) ||
					user.status?.toLowerCase().includes(searchLower) ||
					user.schools?.name?.toLowerCase().includes(searchLower) ||
					user.teacher_grades?.some((tg) =>
						tg.grades?.name?.toLowerCase().includes(searchLower)
					)
			);
		}

		setFilteredUsers(filtered);
		setPage(0); // Reset to first page when filters change
	};

	const handleEditClick = async (user) => {
		try {
			const { data: teacherGrades, error } = await supabase
				.from("teacher_grades")
				.select("grade_id")
				.eq("profile_id", user.id);

			if (error) throw error;

			setSelectedUser(user);
			setEditForm({
				full_name: user.full_name || "",
				email: user.email || "",
				phone_number: user.phone_number || "",
				role: user.role || "",
				school_id: user.school_id || "",
				grade_ids: teacherGrades?.map((tg) => tg.grade_id) || [],
			});
			setOpenDialog(true);
		} catch (err) {
			console.error("Error fetching teacher grades:", err);
		}
	};

	const handleSave = async () => {
		try {
			if (!employeeCanManageProfile(selectedUser)) {
				setSuccessMessage("You are not allowed to update this user.");
				setTimeout(() => setSuccessMessage(""), 4000);
				return;
			}

			if (
				isFunmiyoEmployee &&
				(!editForm.school_id || !assignedSchoolIds.includes(editForm.school_id))
			) {
				setSuccessMessage("Please select a school from your assigned schools.");
				setTimeout(() => setSuccessMessage(""), 4000);
				return;
			}

			const effectiveSchoolId = editForm.school_id;
			const effectiveRole = isFunmiyoEmployee
				? APP_ROLES.SCHOOL_TEACHER
				: editForm.role;

			const { error: profileError } = await supabase
				.from("profiles")
				.update({
					full_name: editForm.full_name,
					email: editForm.email,
					phone_number: editForm.phone_number,
					role: effectiveRole,
					school_id: effectiveSchoolId,
				})
				.eq("id", selectedUser.id);

			if (profileError) throw profileError;

			if (editForm.grade_ids.length > 0) {
				const { error: deleteError } = await supabase
					.from("teacher_grades")
					.delete()
					.eq("profile_id", selectedUser.id);

				if (deleteError) throw deleteError;

				const gradeEntries = editForm.grade_ids.map((gradeId) => ({
					profile_id: selectedUser.id,
					grade_id: gradeId,
					school_id: effectiveSchoolId,
				}));

				const { error: insertError } = await supabase
					.from("teacher_grades")
					.insert(gradeEntries);

				if (insertError) throw insertError;
			}

			setSuccessMessage("User updated successfully");
			await fetchUsers(loggedInUserProfile);
			setOpenDialog(false);

			setTimeout(() => setSuccessMessage(""), 3000);
		} catch (err) {
			console.error("Error updating user:", err);
		}
	};

	const handleStatusChange = async (userId, newStatus) => {
		try {
			const target = users.find((u) => u.id === userId);
			if (!employeeCanManageProfile(target)) {
				console.error("ManageUsers: status change not allowed for this user");
				return;
			}

			const updates = {
				status: newStatus,
				...(newStatus === "Active" && {
					approved_at: new Date().toISOString(),
				}),
				...(newStatus === "Inactive" && {
					deactivated_at: new Date().toISOString(),
				}),
			};

			const { error } = await supabase
				.from("profiles")
				.update(updates)
				.eq("id", userId);

			if (error) throw error;

			setSuccessMessage(`User ${newStatus.toLowerCase()} successfully`);
			await fetchUsers(loggedInUserProfile);
			setTimeout(() => setSuccessMessage(""), 3000);
		} catch (err) {
			console.error("Error updating user status:", err);
		}
	};

	// Pagination handlers
	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleDownloadData = async () => {
		try {
			if (isFunmiyoEmployee && assignedSchoolIds.length === 0) {
				setSuccessMessage(
					"You have no assigned schools. Cannot export."
				);
				setTimeout(() => setSuccessMessage(""), 4000);
				return;
			}

			// Build the query with filters
			let query = supabase.from("schools").select(`
					name,
					profiles!profiles_school_id_fkey (
						full_name,
						email,
						phone_number,
						status,
						role,
						teacher_grades (
							grades (name)
						)
					)
				`);

			if (isFunmiyoEmployee) {
				query = query.in("id", assignedSchoolIds);
			}

			// Apply role filter if selected
			if (filters.role) {
				query = query.eq("profiles.role", filters.role);
			}

			// Apply status filter if selected
			if (filters.status) {
				query = query.eq("profiles.status", filters.status);
			}

			const { data, error } = await query.order("name");

			if (error) throw error;

			// Transform the data to match the SQL query result format
			const csvData = [];
			data.forEach((school) => {
				school.profiles.forEach((profile) => {
					if (
						isFunmiyoEmployee &&
						profile.role !== APP_ROLES.SCHOOL_TEACHER
					) {
						return;
					}

					// Apply additional client-side filtering for search if needed
					let includeProfile = true;

					if (filters.search) {
						const searchLower = filters.search.toLowerCase();
						const matchesSearch =
							profile.email?.toLowerCase().includes(searchLower) ||
							profile.full_name?.toLowerCase().includes(searchLower) ||
							profile.phone_number?.toLowerCase().includes(searchLower) ||
							ROLES[profile.role]?.label.toLowerCase().includes(searchLower) ||
							profile.status?.toLowerCase().includes(searchLower) ||
							school.name?.toLowerCase().includes(searchLower) ||
							profile.teacher_grades?.some((tg) =>
								tg.grades?.name?.toLowerCase().includes(searchLower)
							);

						if (!matchesSearch) {
							includeProfile = false;
						}
					}

					if (includeProfile) {
						if (profile.teacher_grades && profile.teacher_grades.length > 0) {
							profile.teacher_grades.forEach((tg) => {
								csvData.push({
									school_name: school.name,
									teacher_name: profile.full_name || "",
									teacher_email: profile.email || "",
									teacher_phone: profile.phone_number || "",
									grade_name: tg.grades?.name || "",
									status: profile.status || "",
								});
							});
						} else {
							// Include teachers without grades
							csvData.push({
								school_name: school.name,
								teacher_name: profile.full_name || "",
								teacher_email: profile.email || "",
								teacher_phone: profile.phone_number || "",
								grade_name: "",
								status: profile.status || "",
							});
						}
					}
				});
			});

			// Sort the data as per SQL query (by school_name, teacher_name, grade_name)
			csvData.sort((a, b) => {
				if (a.school_name !== b.school_name) {
					return a.school_name.localeCompare(b.school_name);
				}
				if (a.teacher_name !== b.teacher_name) {
					return a.teacher_name.localeCompare(b.teacher_name);
				}
				return a.grade_name.localeCompare(b.grade_name);
			});

			// Create CSV headers
			const headers = [
				"School Name",
				"Teacher Name",
				"Teacher Email",
				"Teacher Phone",
				"Grade Name",
				"Status",
			];

			// Convert to CSV format
			const csvRows = csvData.map((row) => [
				row.school_name,
				row.teacher_name,
				row.teacher_email,
				row.teacher_phone,
				row.grade_name,
				row.status,
			]);

			const csvContent = [
				headers.join(","),
				...csvRows.map((row) =>
					row
						.map((field) =>
							typeof field === "string" && field.includes(",")
								? `"${field.replace(/"/g, '""')}"`
								: field
						)
						.join(",")
				),
			].join("\n");

			// Create filename with filter information
			let filename = "users_data";
			if (filters.role || filters.status || filters.search) {
				const filterParts = [];
				if (filters.role)
					filterParts.push(ROLES[filters.role]?.label || filters.role);
				if (filters.status) filterParts.push(filters.status);
				if (filters.search)
					filterParts.push(`search-${filters.search.substring(0, 10)}`);
				filename += `_${filterParts.join("_").replace(/[^a-zA-Z0-9]/g, "_")}`;
			}
			filename += `_${new Date().toISOString().split("T")[0]}.csv`;

			// Download the CSV
			const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(link);

			setSuccessMessage("Data downloaded successfully");
			setTimeout(() => setSuccessMessage(""), 3000);
		} catch (error) {
			console.error("Error downloading data:", error);
			// You might want to add an error state here
		}
	};

	// Get paginated data
	const paginatedUsers = filteredUsers.slice(
		page * rowsPerPage,
		page * rowsPerPage + rowsPerPage
	);

	return (
		<Box sx={{ p: 2 }}>
			{successMessage && (
				<Alert severity="success" sx={{ mb: 2 }}>
					{successMessage}
				</Alert>
			)}

			{isFunmiyoEmployee && assignedSchoolIds.length === 0 && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					You have no assigned schools. You cannot view teachers until an
					administrator links schools to your account.
				</Alert>
			)}

			{isFunmiyoEmployee && assignedSchoolIds.length > 0 && (
				<Alert severity="info" sx={{ mb: 2 }}>
					You are viewing <strong>teachers</strong> from your{" "}
					{assignedSchoolIds.length} assigned school
					{assignedSchoolIds.length === 1 ? "" : "s"}. You can edit, approve,
					deactivate, or reset passwords — not delete users.
				</Alert>
			)}

			{/* Compact Header with Filters */}
			<Paper sx={{ p: 2, mb: 2 }}>
				<Stack
					direction={{ xs: "column", md: "row" }}
					spacing={2}
					alignItems="center"
					justifyContent="space-between">
					<Typography variant="h6" sx={{ minWidth: "fit-content" }}>
						Manage Users ({filteredUsers.length})
					</Typography>
					<Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
						<TextField
							size="small"
							placeholder="Search users..."
							value={filters.search}
							onChange={(e) =>
								setFilters({ ...filters, search: e.target.value })
							}
							sx={{ minWidth: 200 }}
							InputProps={{
								startAdornment: (
									<SearchIcon
										size={16}
										style={{ marginRight: 8, color: "#666" }}
									/>
								),
							}}
						/>

						{!isFunmiyoEmployee && (
							<FormControl size="small" sx={{ minWidth: 120 }}>
								<InputLabel>Role</InputLabel>
								<Select
									value={filters.role}
									label="Role"
									onChange={(e) =>
										setFilters({ ...filters, role: e.target.value })
									}>
									<MenuItem value="">All</MenuItem>
									{Object.entries(ROLES).map(([value, { label }]) => (
										<MenuItem key={value} value={value}>
											{label}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}

						<FormControl size="small" sx={{ minWidth: 120 }}>
							<InputLabel>Status</InputLabel>
							<Select
								value={filters.status}
								label="Status"
								onChange={(e) =>
									setFilters({ ...filters, status: e.target.value })
								}>
								<MenuItem value="">All</MenuItem>
								<MenuItem value="Active">Active</MenuItem>
								<MenuItem value="Pending Approval">Pending</MenuItem>
								<MenuItem value="Inactive">Inactive</MenuItem>
							</Select>
						</FormControl>

						{/* Add the download button here */}
						<Button
							variant="outlined"
							startIcon={<DownloadIcon size={16} />}
							onClick={handleDownloadData}
							sx={{
								height: "40px",
								minWidth: "fit-content",
								whiteSpace: "nowrap",
							}}>
							Download
						</Button>
					</Stack>
				</Stack>
			</Paper>

			{/* Compact Table */}
			<Paper>
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
								<TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
								<TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
								<TableCell sx={{ fontWeight: 600 }}>School</TableCell>
								<TableCell sx={{ fontWeight: 600 }}>Grades</TableCell>
								<TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
								<TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{paginatedUsers.map((user) => (
								<TableRow key={user.id} hover>
									<TableCell>
										<Box>
											<Typography variant="body2" sx={{ fontWeight: 500 }}>
												{user.full_name || "N/A"}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{user.phone_number || "No phone"}
											</Typography>
										</Box>
									</TableCell>
									<TableCell>
										<Typography variant="body2">{user.email}</Typography>
									</TableCell>
									<TableCell>
										<Chip
											label={ROLES[user.role]?.label || user.role}
											size="small"
											sx={{
												bgcolor: `${ROLES[user.role]?.color}15`,
												color: ROLES[user.role]?.color,
												fontWeight: 500,
												fontSize: "0.75rem",
											}}
										/>
									</TableCell>
									<TableCell>
										<Typography variant="body2" noWrap>
											{user.schools?.name || "N/A"}
										</Typography>
									</TableCell>
									<TableCell>
										{(() => {
											const gradeNames =
												user.teacher_grades
													?.map((tg) => tg.grades?.name)
													.join(", ") || "N/A";
											const truncatedGrades =
												gradeNames.length > 8
													? gradeNames.substring(0, 8) + "..."
													: gradeNames;

											return (
												<Tooltip title={gradeNames} arrow placement="top">
													<Typography
														variant="body2"
														sx={{ cursor: "pointer" }}>
														{truncatedGrades}
													</Typography>
												</Tooltip>
											);
										})()}
									</TableCell>
									<TableCell>
										<Chip
											label={user.status}
											size="small"
											sx={{
												bgcolor: `${STATUS_COLORS[user.status]}15`,
												color: STATUS_COLORS[user.status],
												fontWeight: 500,
												fontSize: "0.75rem",
											}}
										/>
									</TableCell>
									<TableCell>
										<Stack direction="row" spacing={0.5}>
											<IconButton
												size="small"
												onClick={() => handleEditClick(user)}>
												<EditIcon size={14} />
											</IconButton>
											{user.status !== "Active" && (
												<IconButton
													size="small"
													color="success"
													onClick={() => handleStatusChange(user.id, "Active")}>
													<CheckIcon size={14} />
												</IconButton>
											)}
											{user.status !== "Inactive" && (
												<IconButton
													size="small"
													color="error"
													onClick={() =>
														handleStatusChange(user.id, "Inactive")
													}>
													<XIcon size={14} />
												</IconButton>
											)}
											<IconButton
												size="small"
												color="warning"
												onClick={() => {
													setUserToResetPassword(user);
													setResetPasswordDialog(true);
												}}
												title="Reset Password">
												<KeyIcon size={14} />
											</IconButton>
											{isFunmiyoAdmin && (
												<IconButton
													size="small"
													color="error"
													onClick={() => {
														setUserToDelete(user);
														setDeleteDialog(true);
													}}
													title="Delete User">
													<TrashIcon size={14} />
												</IconButton>
											)}
										</Stack>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>

				{filteredUsers.length === 0 && (
					<Box sx={{ py: 4, textAlign: "center" }}>
						<Typography color="text.secondary">
							No users found matching the selected filters.
						</Typography>
					</Box>
				)}

				{/* Pagination */}
				<TablePagination
					component="div"
					count={filteredUsers.length}
					page={page}
					onPageChange={handleChangePage}
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={handleChangeRowsPerPage}
					rowsPerPageOptions={[5, 10, 25, 50]}
					sx={{ borderTop: "1px solid #e0e0e0" }}
				/>
			</Paper>

			{/* Edit Dialog - More Compact */}
			<Dialog
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				maxWidth="sm"
				fullWidth>
				<DialogTitle sx={{ pb: 1 }}>Edit User</DialogTitle>
				<DialogContent sx={{ pt: 1 }}>
					<Stack spacing={2}>
						<TextField
							label="Full Name"
							fullWidth
							size="small"
							value={editForm.full_name}
							onChange={(e) =>
								setEditForm({ ...editForm, full_name: e.target.value })
							}
						/>

						<Stack direction="row" spacing={2}>
							<TextField
								label="Email"
								fullWidth
								size="small"
								value={editForm.email}
								onChange={(e) =>
									setEditForm({ ...editForm, email: e.target.value })
								}
							/>
							<TextField
								label="Phone"
								fullWidth
								size="small"
								value={editForm.phone_number}
								onChange={(e) =>
									setEditForm({ ...editForm, phone_number: e.target.value })
								}
							/>
						</Stack>

						<TextField
							label="Role"
							fullWidth
							size="small"
							select
							disabled={isFunmiyoEmployee}
							value={editForm.role}
							onChange={(e) =>
								setEditForm({ ...editForm, role: e.target.value })
							}>
							{Object.entries(ROLES).map(([value, { label }]) => (
								<MenuItem key={value} value={value}>
									{label}
								</MenuItem>
							))}
						</TextField>

						<Autocomplete
							options={schools}
							disabled={isFunmiyoEmployee}
							getOptionLabel={(school) => school.name || ""}
							value={
								schools.find((school) => school.id === editForm.school_id) ||
								null
							}
							onChange={(event, newValue) => {
								setEditForm({ ...editForm, school_id: newValue?.id || "" });
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									label="School"
									size="small"
									placeholder="Search schools..."
								/>
							)}
							size="small"
						/>

						<TextField
							label="Grades"
							fullWidth
							size="small"
							select
							SelectProps={{
								multiple: true,
								value: editForm.grade_ids,
								onChange: (e) =>
									setEditForm({ ...editForm, grade_ids: e.target.value }),
								renderValue: (selected) => {
									const selectedGrades = grades.filter((grade) =>
										selected.includes(grade.id)
									);
									return selectedGrades.map((grade) => grade.name).join(", ");
								},
							}}>
							{grades.map((grade) => (
								<MenuItem key={grade.id} value={grade.id}>
									{grade.name}
								</MenuItem>
							))}
						</TextField>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ pt: 1 }}>
					<Button onClick={() => setOpenDialog(false)}>Cancel</Button>
					<Button onClick={handleSave} variant="contained">
						Save
					</Button>
				</DialogActions>
			</Dialog>

			{/* Reset Password Confirmation Dialog */}
			<Dialog
				open={resetPasswordDialog}
				onClose={() => !resettingPassword && setResetPasswordDialog(false)}
				maxWidth="sm"
				fullWidth>
				<DialogTitle sx={{ color: "#F59E0B", fontWeight: 600 }}>
					Reset User Password
				</DialogTitle>
				<DialogContent>
					<Alert severity="info" sx={{ mb: 2 }}>
						<Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
							⚠️ Password Reset
						</Typography>
						<Typography variant="body2">
							This will reset the user's password to the default password: <strong>123456</strong>
						</Typography>
					</Alert>

					{userToResetPassword && (
						<Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 1 }}>
							<Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
								<strong>Name:</strong> {userToResetPassword.full_name || "N/A"}
							</Typography>
							<Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
								<strong>Email:</strong> {userToResetPassword.email}
							</Typography>
							<Typography variant="body2" sx={{ color: "#64748b" }}>
								<strong>New Password:</strong> <strong style={{ color: "#F59E0B" }}>123456</strong>
							</Typography>
						</Box>
					)}
				</DialogContent>
				<DialogActions sx={{ p: 3, pt: 1 }}>
					<Button
						onClick={() => setResetPasswordDialog(false)}
						disabled={resettingPassword}
						variant="outlined"
						sx={{
							color: "#64748b",
							borderColor: "#e2e8f0",
							"&:hover": {
								borderColor: "#cbd5e1",
								bgcolor: "#f8fafc",
							},
						}}>
						Cancel
					</Button>
					<Button
						onClick={handleResetPassword}
						disabled={resettingPassword}
						variant="contained"
						sx={{
							bgcolor: "#F59E0B",
							"&:hover": {
								bgcolor: "#D97706",
							},
							"&.Mui-disabled": {
								bgcolor: "#9ca3af",
							},
						}}>
						{resettingPassword ? "Resetting..." : "Reset Password"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={deleteDialog}
				onClose={() => !deleting && setDeleteDialog(false)}
				maxWidth="sm"
				fullWidth>
				<DialogTitle sx={{ color: "#dc2626", fontWeight: 600 }}>
					Delete User
				</DialogTitle>
				<DialogContent>
					<Alert severity="warning" sx={{ mb: 2 }}>
						<Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
							⚠️ This action cannot be undone!
						</Typography>
						<Typography variant="body2">
							This will permanently delete the user and all associated data.
						</Typography>
					</Alert>

					{userToDelete && (
						<Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 1 }}>
							<Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
								<strong>Name:</strong> {userToDelete.full_name || "N/A"}
							</Typography>
							<Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
								<strong>Email:</strong> {userToDelete.email}
							</Typography>
							<Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
								<strong>Role:</strong>{" "}
								{ROLES[userToDelete.role]?.label || userToDelete.role}
							</Typography>
							<Typography variant="body2" sx={{ color: "#64748b" }}>
								<strong>School:</strong> {userToDelete.schools?.name || "N/A"}
							</Typography>
						</Box>
					)}
				</DialogContent>
				<DialogActions sx={{ p: 3, pt: 1 }}>
					<Button
						onClick={() => setDeleteDialog(false)}
						disabled={deleting}
						variant="outlined"
						sx={{
							color: "#64748b",
							borderColor: "#e2e8f0",
							"&:hover": {
								borderColor: "#cbd5e1",
								bgcolor: "#f8fafc",
							},
						}}>
						Cancel
					</Button>
					<Button
						onClick={handleDeleteUser}
						disabled={deleting}
						variant="contained"
						sx={{
							bgcolor: "#dc2626",
							"&:hover": {
								bgcolor: "#b91c1c",
							},
							"&.Mui-disabled": {
								bgcolor: "#9ca3af",
							},
						}}>
						{deleting ? "Deleting..." : "Delete Permanently"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default ManageUsers;
